import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import type { ColDef, GridOptions, RowSelectionOptions, ValueFormatterParams, GetContextMenuItemsParams, MenuItemDef, GridReadyEvent, IRowNode } from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { clsx } from 'clsx';

import { BBookCharts } from '@/components/charts/BBookCharts';
import {
  mt5Api, connectBBookWebSocket, type MT5PositionWithNode, type MT5NodeAPI, type BBookWsEvent,
  ctraderApi, ctraderDataApi, connectSystemHealthWebSocket,
  type CTraderPositionRow, type CTraderPositionMessage, type CTraderNodeWsEvent,
} from '@/services/api';
import { fmtHdrMoney, fmtHdrCompact, pnlColor, usePortfolioStats } from '@/stores/PortfolioStatsContext';
import { CardsPeriodToggle } from '@/components/portfolio/CardsPeriodToggle';

// ======================
// THEME (Quartz dark)
// ======================
const gridTheme = themeQuartz.withParams({
  backgroundColor: '#232326',
  browserColorScheme: 'dark',
  chromeBackgroundColor: { ref: 'foregroundColor', mix: 0.11, onto: 'backgroundColor' },
  fontSize: 14,
  foregroundColor: '#FFF',
  headerFontSize: 14,
});

// ======================
// TYPES
// ======================
export type BBookPlatform = 'MT5' | 'cTrader';
type PlatformFilter = 'All' | BBookPlatform;

export interface BBookPosition {
  platform: BBookPlatform;
  login: number;       // 0 when cTrader holds no account for the position
  symbol: string;
  position_id: number;
  time: string;        // ISO string (derived from time_create unix seconds)
  type: 'BUY' | 'SELL';
  volume: number;      // volume_lots
  price_open: number;
  sl: number | null;   // price_sl — 0 mapped to null
  tp: number | null;   // price_tp — 0 mapped to null
  price_current: number | null;   // cTrader: null while there is no quote for the symbol
  profit: number | null;          // USD, broker view. cTrader: null without a price or a rate to USD
  /** cTrader: broker-view profit in the account's own currency (`currency`). MT5: null. */
  profit_account: number | null;
  currency: string;               // cTrader: the account's currency. MT5: ''
  swap: number | null;            // USD. cTrader: null without a rate to USD
  commission: number | null;      // USD. cTrader: null without a rate to USD
  hedge: 'No' | 'Rule' | 'Manual';   // reserved for future hedge logic
  lp: string | null;                  // reserved for future LP routing
  group: string;                      // MT5 group (not provided per-position; shown if available)
  server: string;                     // node name
  node_id?: number;                   // cTrader rows: the node the position is on
}

export type BBookStreamMsg = {
  upsert?: BBookPosition[];
  remove?: string[];
};

// ======================
// WS CONNECTION STATUS
// ======================
type WsStatus = 'connecting' | 'live' | 'reconnecting' | 'error';

// ======================
// FIELD MAPPING
// ======================
function mapPosition(raw: MT5PositionWithNode): BBookPosition {
  return {
    platform:      'MT5',
    login:         raw.login,
    symbol:        raw.symbol,
    position_id:   raw.position_id,
    time:          new Date(raw.time_create * 1000).toISOString(),
    type:          raw.action === 'BUY' ? 'SELL' : 'BUY',  // broker takes opposite side
    volume:        raw.volume_lots,
    price_open:    raw.price_open,
    price_current: raw.price_current,
    sl:            raw.price_sl  !== 0 ? raw.price_sl  : null,
    tp:            raw.price_tp  !== 0 ? raw.price_tp  : null,
    profit:        -raw.profit,  // broker P&L is inverse of client
    profit_account: null,
    currency:      '',
    swap:          raw.swap,
    commission:    raw.commission,
    hedge:         'No',
    lp:            null,
    group:         '',
    server:        raw.nodeName,
  };
}

/**
 * A cTrader open position (book B) as a B-Book row. Field names match MT5's by
 * design of the Node Data API, so the mapping mirrors mapPosition. A current
 * price or profit the service cannot compute stays empty: null, never 0.
 *
 * Money is shown in USD, as for MT5, so a column adds up across platforms:
 * profit from `profit_usd`, swap and commission at `usd_rate` (Data API 1.4).
 * An account currency without a rate to USD leaves them empty. Before 1.4 the
 * fields are absent; a USD account then needs no rate.
 */
const round2 = (v: number) => Math.round(v * 100) / 100;
function usdRateOf(raw: CTraderPositionRow): number | null {
  if (raw.usd_rate !== undefined) return raw.usd_rate;
  return raw.currency === 'USD' ? 1 : null;
}
function mapCTraderPosition(raw: CTraderPositionRow): BBookPosition {
  const rate = usdRateOf(raw);
  const profitUsd = raw.profit_usd !== undefined
    ? raw.profit_usd
    : (raw.profit !== null && rate !== null ? round2(raw.profit * rate) : null);
  return {
    platform:      'cTrader',
    login:         raw.login ?? 0,
    symbol:        raw.symbol || `#${raw.symbol_id}`,
    position_id:   raw.position_id,
    time:          new Date(raw.time_create * 1000).toISOString(),
    type:          raw.action === 'BUY' ? 'SELL' : 'BUY',  // broker takes opposite side
    volume:        raw.volume_lots ?? 0,
    price_open:    raw.price_open,
    price_current: raw.price_current,
    sl:            raw.price_sl,
    tp:            raw.price_tp,
    profit:        profitUsd === null ? null : -profitUsd,   // broker P&L is inverse of client
    profit_account: raw.profit === null ? null : -raw.profit,
    currency:      raw.currency,
    swap:          rate === null ? null : round2(raw.swap * rate),
    commission:    rate === null ? null : round2(raw.commission * rate),
    hedge:         'No',
    lp:            null,
    group:         raw.group,
    server:        raw.node_name,
    node_id:       raw.node_id,
  };
}

// ======================
// HELPERS
// ======================
const fmtNum = (dp: number) => (p: ValueFormatterParams) => {
  const v = p.value;
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(dp);
};

const fmtPrice = (p: ValueFormatterParams) => {
  const v = p.value;
  const sym = p.data?.symbol || '';
  if (v === null || v === undefined) return '';
  if (sym.includes('JPY')) return Number(v).toFixed(3);
  if (sym.includes('XAU') || sym.includes('BTC')) return Number(v).toFixed(2);
  return Number(v).toFixed(5);
};

// MT5 ids are unchanged. A cTrader position is keyed by (node, position id),
// as the Node Data API keys it: its login and symbol name can change while it
// is open. The prefix keeps the two platforms' ids apart.
const getRowId = (r: BBookPosition) =>
  r.platform === 'cTrader'
    ? `CT|${r.node_id}|${r.position_id}`
    : `${r.position_id}|${r.login}|${r.symbol}`;

// Stable empty reference for the grid's rowData. The grid row model is the
// source of truth for live rows and is fed exclusively via transactions, so
// rowData must never be re-bound from React state after mount.
const EMPTY_ROWS: BBookPosition[] = [];

// ======================
// useBBookWebSocket hook
// ======================
/**
 * Manages the WebSocket connection to the B-Book feed.
 * Reconnects with exponential back-off (1s → 2s → 4s … cap 30s).
 * On every (re)connect the backend automatically sends a SNAPSHOT,
 * so no separate REST fetch is needed on reconnect.
 */
function useBBookWebSocket(opts: {
  onSnapshot:  (positions: BBookPosition[]) => void;
  onUpsert:    (position: BBookPosition)    => void;
  onMerge:     (delta: Partial<MT5PositionWithNode> & { position_id: number }) => void;
  onRemove:    (positionId: number)         => void;
  onStatus:    (s: WsStatus)                => void;
  onCTrader:   (msg: CTraderPositionMessage) => void;
}) {
  const retryRef      = useRef(0);
  const mountedRef    = useRef(true);
  const timerRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupRef    = useRef<(() => void) | null>(null);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    opts.onStatus('connecting');

    const cleanup = connectBBookWebSocket(
      (ev: BBookWsEvent) => {
        if (ev.type === 'SNAPSHOT') {
          const raw = ev.data as MT5PositionWithNode[];
          opts.onSnapshot(raw.map(mapPosition));

        } else if (ev.type === 'POSITION_ADD') {
          // Full position object — safe to map fully
          opts.onUpsert(mapPosition(ev.data as MT5PositionWithNode));

        } else if (ev.type === 'POSITION_CHANGE') {
          // Backend sends only changed fields — merge into existing row
          const d = ev.data as Partial<MT5PositionWithNode> & { position_id: number };
          opts.onMerge(d);

        } else if ((ev as any).type === 'POSITION_BATCH' || (ev as any).data?.type === 'POSITION_BATCH') {
          // Per-tick P/L stream (new). One array per symbol, ~every 500ms.
          // Each element is the same changed-fields delta as a single
          // POSITION_CHANGE, so reuse the merge path and price_current keeps
          // updating. Wire shape unverified: the discriminator and the array
          // may sit on the envelope or inside data, so accept either placement.
          const batch = ((ev as any).data?.positions ?? (ev as any).positions) as
            (Partial<MT5PositionWithNode> & { position_id: number })[] | undefined;
          if (Array.isArray(batch)) {
            for (const d of batch) opts.onMerge(d);
          }

        } else if (ev.type === 'POSITION_DELETE') {
          const d = ev.data as { position_id: number };
          opts.onRemove(d.position_id);
        }
        // subscribed / pong ACKs silently ignored
      },
      (status) => {
        if (status === 'open') {
          retryRef.current = 0;
          opts.onStatus('live');
        } else if (status === 'closed' || status === 'error') {
          if (!mountedRef.current) return;
          opts.onStatus(status === 'error' ? 'error' : 'reconnecting');
          const delay = Math.min(1000 * 2 ** retryRef.current, 30_000);
          retryRef.current++;
          timerRef.current = setTimeout(() => {
            if (mountedRef.current) connect();
          }, delay);
        }
      },
      (msg) => opts.onCTrader(msg),
    );

    cleanupRef.current = cleanup;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      cleanupRef.current?.();
    };
  }, [connect]);
}

// ======================
// COMPONENT
// ======================
export function BBookPage() {
  const gridRef = useRef<AgGridReact<BBookPosition>>(null);
  const [chartsCollapsed, setChartsCollapsed] = useState(false);

  // ── Data state ──────────────────────────────────────────────
  // GRID-AUTHORITATIVE MODEL
  // The AG Grid row model is the single source of truth for live rows. Tick
  // updates are pushed via applyTransactionAsync, which the grid batches on
  // its own render cadence (asyncTransactionWaitMillis) — so React does NOT
  // re-render per tick. The states below are SLOW read-back snapshots taken
  // from the grid every few seconds, used only by the charts, header-card
  // stats and filter dropdowns; none of those need tick-rate freshness.
  const [positions,         setPositions]         = useState<BBookPosition[]>([]); // all rows (slow)
  const [filteredPositions, setFilteredPositions] = useState<BBookPosition[]>([]); // filtered+sorted (slow)
  const [rowCount,          setRowCount]          = useState(0);                   // live grid row count
  const [activeNodes,       setActiveNodes]       = useState<MT5NodeAPI[]>([]);
  const [wsStatus,          setWsStatus]          = useState<WsStatus>('connecting');
  const [error,             setError]             = useState<string | null>(null);

  // ── Platforms ───────────────────────────────────────────────
  // MT5 rows are live on topic mt5.position, cTrader rows on topic
  // ctrader.position: the book-B positions of the cTrader MASTER. Nothing is
  // polled. Only the two MASTER nodes count.
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('All');   // default: All
  const [gridReady,   setGridReady]   = useState(false);
  const [mt5MasterUp, setMt5MasterUp] = useState<boolean | null>(null);   // null: not known yet
  // undefined: not known yet · null: no cTrader MASTER
  const [ctMaster,    setCtMaster]    = useState<{ id: number; name: string; status: string } | null | undefined>(undefined);
  const ctMasterRef = useRef(ctMaster);
  ctMasterRef.current = ctMaster;

  // Filter state
  const [groupInput,   setGroupInput]   = useState('');
  const [symbolInput,  setSymbolInput]  = useState('');
  // Defaults to '' until the master node is resolved, then snaps to the master's name.
  // No "All Servers" option — single-node-at-a-time only.
  const [filterServer, setFilterServer] = useState<string>('');

  // ?login_ids=7001,7002 — set by the Cockpit "Critical traders" link. Trader
  // risk is MT5 only (cTrader traders are not stored), so the logins narrow
  // the MT5 rows; a cTrader account with the same number is not shown.
  const [searchParams, setSearchParams] = useSearchParams();
  const loginIds = useMemo(
    () => (searchParams.get('login_ids') ?? '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0),
    [searchParams],
  );
  const clearLoginIds = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('login_ids');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Lots / Units toggle for the Volume cell on the B-Book card.
  const [volumeDisplayMode, setVolumeDisplayMode] = useState<'Lots' | 'Units'>('Lots');

  // Period-scoped values (Realized P/L, Cost components, Unrealized P/L delta,
  // C-Book volume for the hedge ratio) come from the shared Portfolio summary
  // hook. The TopBar period selector ("Today" / "This Month") drives both this
  // page and the Portfolio page from the same state — no local subscription.
  const portfolio = usePortfolioStats();

  // position_id -> rowId. POSITION_CHANGE / POSITION_DELETE arrive carrying only
  // position_id, but the grid is keyed by the composite getRowId. This index
  // resolves the node for those partial events. Rebuilt on every reconcile and
  // kept in sync on add/remove. Touched only from the WS callback thread.
  const pidToRowIdRef = useRef<Map<number, string>>(new Map());

  // Toolbar filter values mirrored into a ref so the grid's external-filter
  // callbacks always read current values without being re-bound on each render.
  const filtersRef = useRef<{ group: string; symbol: string; server: string; platform: PlatformFilter; logins: Set<number> }>(
    { group: '', symbol: '', server: '', platform: 'All', logins: new Set() },
  );
  useEffect(() => {
    filtersRef.current = {
      group: groupInput, symbol: symbolInput, server: filterServer, platform: platformFilter,
      logins: new Set(loginIds),
    };
    gridRef.current?.api?.onFilterChanged();
  }, [groupInput, symbolInput, filterServer, platformFilter, loginIds]);

  // Does a row pass the toolbar filters. Mirrors the previous React-side filter
  // exactly; reads filtersRef so it never goes stale inside the grid callback.
  const rowPassesFilters = useCallback((p: BBookPosition): boolean => {
    const { group, symbol, server, platform, logins } = filtersRef.current;
    if (platform !== 'All' && p.platform !== platform) return false;
    if (logins.size > 0 && !(p.platform === 'MT5' && logins.has(p.login))) return false;
    const gTerm = group.trim().toUpperCase();
    const sTerm = symbol.trim().toUpperCase();
    if (gTerm) {
      const matchesGroup = p.group.toUpperCase().includes(gTerm);
      const matchesLogin = String(p.login).includes(gTerm);
      if (!matchesGroup && !matchesLogin) return false;
    }
    if (sTerm && !p.symbol.toUpperCase().includes(sTerm)) return false;
    // filterServer is empty only during initial load (before the master node
    // resolves). In that window everything shows; then it narrows to the node.
    // It narrows the MT5 rows to the MT5 master; cTrader rows are read from
    // the cTrader master only, so they need no such narrowing.
    if (p.platform === 'MT5' && server !== '' && p.server !== server) return false;
    return true;
  }, []);

  // One-time node list fetch (nodes don't change at position frequency)
  useEffect(() => {
    mt5Api.getNodes()
      .then(({ nodes }) => {
        const connected = nodes.filter(n => n.connection_status === 'CONNECTED');
        setActiveNodes(connected);
        // Default the selector to the MASTER node. If for any reason no node
        // is flagged as master, fall back to the first connected node so the
        // page still renders something sensible.
        const master = connected.find(n => n.is_master) ?? connected[0];
        if (master) setFilterServer(prev => prev === '' ? master.node_name : prev);
        // First word on the MT5 master; the health feed below keeps it current.
        setMt5MasterUp(prev => prev ?? connected.some(n => n.is_master));
      })
      .catch(() => { setMt5MasterUp(prev => prev ?? false); /* non-fatal — node badges just won't show */ });
  }, []);

  // ── Slow read-back from the grid (charts / stats / dropdowns / overlays) ──
  // Off the tick path. Pulls the grid's current row model into React state so
  // charts, header-card stats, dropdown options and the empty/loading overlays
  // have data — none of which need tick-rate freshness. Called on a 5s timer
  // and once synchronously after each structural reconcile so the first seed
  // shows immediately.
  const pullFromGrid = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const all:      BBookPosition[] = [];
    const filtered: BBookPosition[] = [];
    api.forEachNode(n => { if (n.data) all.push(n.data); });
    api.forEachNodeAfterFilterAndSort(n => { if (n.data) filtered.push(n.data); });
    setPositions(all);
    setFilteredPositions(filtered);
    setRowCount(all.length);
  }, []);

  // ── WebSocket handlers — all mutate the grid via transactions, never state ──

  // Reconcile the grid against an authoritative full set (initial seed AND
  // every reconnect SNAPSHOT): add missing rows, update existing ones, remove
  // rows no longer present. applyTransaction preserves sort, filters, column
  // state, selection and scroll position — so a reconnect snapshot does not
  // disturb the user's grid state.
  // A reconcile covers ONE platform: rows of the other platform are left alone.
  const reconcile = useCallback((rows: BBookPosition[], platform: BBookPlatform = 'MT5') => {
    const api = gridRef.current?.api;
    if (!api) return;

    const incoming = new Map<string, BBookPosition>();
    for (const r of rows) incoming.set(getRowId(r), r);

    const add:    BBookPosition[] = [];
    const update: BBookPosition[] = [];
    const remove: BBookPosition[] = [];

    api.forEachNode(node => {
      const id = node.id;
      if (id !== undefined && node.data && node.data.platform === platform && !incoming.has(id)) remove.push(node.data);
    });
    incoming.forEach((row, id) => {
      (api.getRowNode(id) ? update : add).push(row);
    });

    api.applyTransaction({ add, update, remove });

    // The index serves the MT5 live events only (they carry a bare position_id).
    if (platform === 'MT5') {
      const m = new Map<number, string>();
      incoming.forEach((row, id) => m.set(row.position_id, id));
      pidToRowIdRef.current = m;
    }

    pullFromGrid();   // reflect the seed/reconcile immediately (sync transaction)
  }, [pullFromGrid]);

  const handleSnapshot = useCallback((snap: BBookPosition[]) => {
    setError(null);
    reconcile(snap);
  }, [reconcile]);

  const handleUpsert = useCallback((pos: BBookPosition) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const id = getRowId(pos);
    api.applyTransactionAsync(api.getRowNode(id) ? { update: [pos] } : { add: [pos] });
    pidToRowIdRef.current.set(pos.position_id, id);
  }, []);

  const handleMerge = useCallback((delta: Partial<MT5PositionWithNode> & { position_id: number }) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const id = pidToRowIdRef.current.get(delta.position_id);
    if (!id) return;                       // unknown position — wait for snapshot/add
    const node = api.getRowNode(id);
    if (!node || !node.data) return;
    // Merge only the live fields, exactly as before (broker P&L is inverted).
    const merged: BBookPosition = {
      ...node.data,
      ...(delta.price_current !== undefined && { price_current: delta.price_current }),
      ...(delta.profit        !== undefined && { profit: -delta.profit }),
      ...(delta.swap          !== undefined && { swap: delta.swap }),
      ...(delta.commission    !== undefined && { commission: delta.commission }),
    };
    api.applyTransactionAsync({ update: [merged] });
  }, []);

  const handleRemove = useCallback((positionId: number) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const id = pidToRowIdRef.current.get(positionId);
    if (!id) return;
    const node = api.getRowNode(id);
    if (node && node.data) api.applyTransactionAsync({ remove: [node.data] });
    pidToRowIdRef.current.delete(positionId);
  }, []);

  // ── cTrader positions (topic ctrader.position) ───────────────────────────
  // Every started cTrader node's open positions are held here, followed by the
  // four rules of the Node Data API. The grid shows those of the MASTER in book
  // B; a promotion only changes which node that is. A position is keyed by
  // (node_id, position_id).
  const ctStoreRef = useRef<Map<number, Map<number, CTraderPositionRow>>>(new Map());
  const ctKnownRef = useRef(false);                 // a SNAPSHOT has arrived
  const [ctKnown, setCtKnown] = useState(false);

  const ctShown = (r: CTraderPositionRow) => {
    const m = ctMasterRef.current;
    return !!m && r.node_id === m.id && r.book === 'B';
  };

  // Put the grid's cTrader rows in line with the store.
  const ctRebuild = useCallback(() => {
    const rows: BBookPosition[] = [];
    for (const node of ctStoreRef.current.values()) {
      for (const r of node.values()) if (ctShown(r)) rows.push(mapCTraderPosition(r));
    }
    reconcile(rows, 'cTrader');
  }, [reconcile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCTrader = useCallback((msg: CTraderPositionMessage) => {
    const store = ctStoreRef.current;
    if (msg.type === 'SNAPSHOT') {
      store.clear();
      for (const r of msg.data.positions ?? []) {
        if (!store.has(r.node_id)) store.set(r.node_id, new Map());
        store.get(r.node_id)!.set(r.position_id, r);
      }
      ctKnownRef.current = true;
      setCtKnown(true);
      ctRebuild();
      return;
    }
    if (msg.type === 'POSITION_SYNC') {
      const m = new Map<number, CTraderPositionRow>();
      for (const r of msg.data.positions ?? []) m.set(r.position_id, r);
      store.set(msg.data.node_id, m);
      if (ctMasterRef.current?.id === msg.data.node_id) ctRebuild();
      return;
    }
    if (msg.type === 'POSITION_PRICES') {
      // Two fields of rows already held. Entries whose row is not held are ignored.
      const node = store.get(msg.data.node_id);
      if (!node) return;
      const shown = ctMasterRef.current?.id === msg.data.node_id;
      const api = gridRef.current?.api;
      const update: BBookPosition[] = [];
      for (const e of msg.data.prices ?? []) {
        const held = node.get(e.position_id);
        if (!held) continue;
        const next = {
          ...held,
          price_current: e.price_current,
          profit: e.profit,
          ...(e.profit_usd !== undefined && { profit_usd: e.profit_usd }),
          ...(e.usd_rate !== undefined && { usd_rate: e.usd_rate }),
        };
        node.set(e.position_id, next);
        if (shown && api && next.book === 'B') {
          const row = mapCTraderPosition(next);
          if (api.getRowNode(getRowId(row))) update.push(row);
        }
      }
      if (update.length) api!.applyTransactionAsync({ update });
      return;
    }
    const r = msg.data;
    if (!store.has(r.node_id)) store.set(r.node_id, new Map());
    const node = store.get(r.node_id)!;
    if (msg.type === 'POSITION_DELETE') node.delete(r.position_id);
    else node.set(r.position_id, r);

    const api = gridRef.current?.api;
    if (!api || ctMasterRef.current?.id !== r.node_id) return;
    const row = mapCTraderPosition(r);
    const existing = api.getRowNode(getRowId(row));
    if (msg.type !== 'POSITION_DELETE' && r.book === 'B') {
      api.applyTransactionAsync(existing ? { update: [row] } : { add: [row] });
    } else if (existing?.data) {
      // Closed, or moved to book A.
      api.applyTransactionAsync({ remove: [existing.data] });
    }
  }, [ctRebuild]);

  useBBookWebSocket({
    onSnapshot: handleSnapshot,
    onUpsert:   handleUpsert,
    onMerge:    handleMerge,
    onRemove:   handleRemove,
    onStatus:   setWsStatus,
    onCTrader:  handleCTrader,
  });

  // Initial REST load — reconciles into the grid (same path as a SNAPSHOT) so
  // rows appear immediately regardless of WS snapshot timing.
  const fetchPositions = useCallback(async () => {
    setError(null);
    try {
      const { positions: raw, nodes } = await mt5Api.getAllBBookPositions();
      setActiveNodes(nodes);
      reconcile(raw.map(p => mapPosition(p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load B-Book positions');
    }
  }, [reconcile]);

  // The grid follows the store once it exists, and whenever the MASTER changes.
  useEffect(() => {
    if (gridReady) ctRebuild();
  }, [gridReady, ctMaster?.id, ctRebuild]);

  // First paint before the socket's SNAPSHOT, from the MASTER's list route;
  // the SNAPSHOT replaces it. Read once per MASTER, never on a timer.
  useEffect(() => {
    const master = ctMaster;
    if (!gridReady || !master || ctKnownRef.current) return;
    let cancelled = false;
    (async () => {
      const rows: CTraderPositionRow[] = [];
      try {
        for (let offset = 0; ;) {
          const res = await ctraderDataApi.getPositions(master.id, { book: 'B', limit: 1000, offset });
          if (cancelled || ctKnownRef.current) return;
          for (const p of res.positions ?? []) rows.push({ ...p, platform: 'CTRADER', node_id: master.id, node_name: master.name });
          offset += res.returned;
          if (!res.returned || offset >= res.total) break;
        }
      } catch { return; }   // 503: the node is not started and holds nothing; the socket will say so
      if (cancelled || ctKnownRef.current) return;
      const m = new Map<number, CTraderPositionRow>();
      for (const r of rows) m.set(r.position_id, r);
      ctStoreRef.current.set(master.id, m);
      ctRebuild();
    })();
    return () => { cancelled = true; };
  }, [gridReady, ctMaster?.id, ctRebuild]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Master state of both platforms, live ─────────────────────────────────
  // One socket: system.health says whether the MT5 master is connected (the
  // same source as the bottom bar), and topic ctrader.node_status carries the
  // cTrader master's state.
  useEffect(() => {
    let cancelled = false;
    let events = 0;
    let openedBefore = false;

    const seedCTraderMaster = () => {
      const at = events;
      ctraderApi.getMaster()
        .then((res) => {
          if (cancelled || events !== at) return;      // an event overtook the read
          const m = res.master;
          setCtMaster(m ? { id: m.node_id, name: m.node_name, status: m.connection_status } : null);
        })
        .catch(() => { if (!cancelled) setCtMaster(prev => (prev === undefined ? null : prev)); });
    };

    const onCTraderEvent = (e: CTraderNodeWsEvent) => {
      events += 1;
      if (e.kind === 'snapshot') {
        const m = e.nodes.find(n => n.is_master || n.node_type === 'MASTER');
        setCtMaster(m ? { id: m.node_id, name: m.node_name, status: m.connection_status } : null);
        return;
      }
      if (e.kind === 'type_change') {
        if (e.promoted.new_type === 'MASTER') {
          setCtMaster({ id: e.promoted.node_id, name: e.promoted.node_name, status: 'CONNECTING' });
        }
        return;
      }
      // The positions themselves arrive on ctrader.position; here only the
      // MASTER's connection state is followed.
      const node = e.node;
      const current = ctMasterRef.current;
      if (node) {
        if (node.node_type !== 'MASTER' && !node.is_master) return;
        if (current && current.id === node.node_id && current.name === node.node_name && current.status === node.connection_status) return;
        setCtMaster({ id: node.node_id, name: node.node_name, status: node.connection_status });
      } else if (e.kind === 'status' && current && current.id === e.nodeId && current.status !== e.status) {
        setCtMaster({ ...current, status: e.status });
      }
    };

    seedCTraderMaster();
    const stop = connectSystemHealthWebSocket(
      (p) => {
        const up = !!p.master?.connected;
        setMt5MasterUp(prev => (prev === up ? prev : up));
      },
      (status) => {
        if (status !== 'open') return;
        // Events sent while the socket was down are lost: read the master again.
        if (openedBefore) seedCTraderMaster();
        openedBefore = true;
      },
      onCTraderEvent,
    );
    return () => { cancelled = true; stop(); };
  }, []);

  const mt5Active = mt5MasterUp === true;
  const ctActive  = !!ctMaster && ctMaster.status === 'CONNECTED';

  // Counts, stats and the empty-state text follow a platform change at once.
  useEffect(() => { pullFromGrid(); }, [platformFilter, pullFromGrid]);

  // Slow refresh so price drift reflects in charts/stats between structural
  // changes. Structural changes (seed/reconnect) update immediately via
  // reconcile -> pullFromGrid; this timer just keeps prices reasonably fresh.
  useEffect(() => {
    const t = setInterval(pullFromGrid, 5000);
    return () => clearInterval(t);
  }, [pullFromGrid]);

  // ── Derived filter options (from the slow snapshot) ─────────────────────
  const uniqueLogins  = useMemo(() => Array.from(new Set(positions.map(p => String(p.login)))).sort(), [positions]);
  const uniqueSymbols = useMemo(() => Array.from(new Set(positions.map(p => p.symbol))).sort(), [positions]);
  const uniqueGroups  = useMemo(() => Array.from(new Set(positions.map(p => p.group).filter(Boolean))).sort(), [positions]);
  const uniqueServers = useMemo(() => activeNodes.map(n => n.node_name).sort(), [activeNodes]);

  // ======================
  // COLUMN DEFINITIONS
  // ======================
  const columnDefs = useMemo<ColDef<BBookPosition>[]>(() => [
    { field: 'platform',    headerName: 'Platform',   filter: 'agSetColumnFilter',    width: 105, pinned: 'left', filterParams: { values: ['MT5', 'cTrader'] } },
    { field: 'login',       headerName: 'Login ID',   filter: 'agNumberColumnFilter', width: 120, pinned: 'left',
      valueFormatter: (p) => (p.value === 0 ? '—' : String(p.value ?? '')) },
    { field: 'symbol',      headerName: 'Symbol',     filter: 'agSetColumnFilter',    width: 110, pinned: 'left' },
    { field: 'position_id', headerName: 'Position ID', filter: 'agNumberColumnFilter', width: 120 },
    {
      field: 'time',
      headerName: 'Open Time',
      filter: 'agDateColumnFilter',
      sort: 'desc',
      width: 160,
      valueFormatter: (p) => p.value
        ? new Date(p.value).toLocaleString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
          }).replace(',', '')
        : '',
    },
    {
      field: 'type',
      headerName: 'Type',
      filter: 'agSetColumnFilter',
      width: 90,
      // Colored text via cellStyle instead of a JSX cellRenderer. Function
      // cell renderers can't refresh in place, so under the high-frequency
      // transaction stream AG Grid tears down and rebuilds the React-rendered
      // cell on each update, leaking the old fiber + per-cell tooltip
      // controller. valueFormatter/cellStyle keep cells as reusable DOM.
      cellStyle: (p) => ({ color: p.value === 'BUY' ? '#49b3b3' : '#e0a020' }),
    },
    { field: 'volume',        headerName: 'Volume',   filter: 'agNumberColumnFilter', valueFormatter: fmtNum(2),  width: 100, type: 'rightAligned' },
    { field: 'price_open',    headerName: 'Open Price', filter: 'agNumberColumnFilter', valueFormatter: fmtPrice,   width: 110, type: 'rightAligned' },
    { field: 'sl',            headerName: 'S/L',      filter: 'agNumberColumnFilter', valueFormatter: fmtPrice,   width: 110, type: 'rightAligned' },
    { field: 'tp',            headerName: 'T/P',      filter: 'agNumberColumnFilter', valueFormatter: fmtPrice,   width: 110, type: 'rightAligned' },
    { field: 'price_current', headerName: 'Current',  filter: 'agNumberColumnFilter', valueFormatter: fmtPrice,   width: 110, type: 'rightAligned' },
    {
      field: 'profit',
      headerName: 'Profit',
      filter: 'agNumberColumnFilter',
      width: 120,
      type: 'rightAligned',
      // profit updates on EVERY tick. Render as formatted/colored text via
      // valueFormatter + cellStyle (no JSX) so the cell is refreshed in place
      // rather than recreated per tick — this is the hot column and the main
      // source of the residual tooltip/fiber retention.
      valueFormatter: (p) => {
        if (p.value === null || p.value === undefined) return '—';   // not sent (cTrader)
        const v = p.value as number;
        return (v >= 0 ? '$' : '-$') + Math.abs(v).toFixed(2);
      },
      cellStyle: (p) => {
        const v = (p.value as number) ?? 0;
        return { color: v > 0 ? '#66e07a' : v < 0 ? '#ff5c5c' : '#999' };
      },
    },
    {
      // cTrader only, hidden by default (Columns panel): the profit in the
      // account's own currency, as cTrader shows it, broker view.
      field: 'profit_account',
      headerName: 'Profit (account ccy)',
      hide: true,
      filter: 'agNumberColumnFilter',
      width: 150,
      type: 'rightAligned',
      valueFormatter: (p) => (p.value === null || p.value === undefined
        ? '' : `${(p.value as number).toFixed(2)} ${p.data?.currency ?? ''}`.trim()),
    },
    { field: 'swap',       headerName: 'Swap',       filter: 'agNumberColumnFilter', valueFormatter: fmtNum(2), width: 90,  type: 'rightAligned' },
    { field: 'commission', headerName: 'Commission', filter: 'agNumberColumnFilter', valueFormatter: fmtNum(2), width: 110, type: 'rightAligned' },
    { field: 'hedge',  headerName: 'Hedge',  filter: 'agSetColumnFilter', width: 90, filterParams: { values: ['No', 'Rule', 'Manual'] } },
    { field: 'lp',     headerName: 'LP',     filter: 'agSetColumnFilter', width: 90, valueFormatter: (p: ValueFormatterParams) => p.value || '' },
    { field: 'group',  headerName: 'Group',  filter: 'agSetColumnFilter', width: 90  },
    { field: 'server', headerName: 'Server', filter: 'agSetColumnFilter', width: 140 },
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 80,
    flex: 1,
  }), []);

  const rowSelection = useMemo<RowSelectionOptions>(() => ({
    mode: 'multiRow',
    enableClickSelection: true,
    checkboxes: true,
    headerCheckbox: true,
  }), []);

  const gridOptions = useMemo<GridOptions<BBookPosition>>(() => ({
    enableAdvancedFilter: true,
    sideBar: { toolPanels: ['columns', 'filters'], defaultToolPanel: '' },
    columnHoverHighlight: true,
    animateRows: false,
    rowBuffer: 20,
    debounceVerticalScrollbar: true,
    // Coalesce async transactions: the grid flushes batched applyTransactionAsync
    // calls on this cadence regardless of how fast ticks arrive. At a 25ms LP
    // feed this turns ~40 updates/sec/position into ~20 batched flushes/sec
    // total, which is what makes the high-frequency feed safe to render.
    asyncTransactionWaitMillis: 50,
    // Use native browser title tooltips instead of AG Grid's custom tooltip
    // system. The custom path instantiates a per-cell tooltipCtrl bound to a
    // listener (native_bind) that is retained by tooltipManager when cells are
    // destroyed under the high-frequency transaction stream — the root of the
    // detached-cell retention seen in heap snapshots. Native tooltips are
    // browser-managed and cannot leak.
    enableBrowserTooltips: true,
    // The page draws its own empty-state text over the grid; the grid's
    // "No Rows To Show" would print on top of it.
    suppressNoRowsOverlay: true,
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent' },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agAggregationComponent' },
      ],
    },
  }), []);

  // External (toolbar) filter — runs inside the grid so live transaction
  // updates are filtered in place without ever re-binding rowData.
  const isExternalFilterPresent = useCallback(() => {
    const { group, symbol, server, platform, logins } = filtersRef.current;
    return group.trim() !== '' || symbol.trim() !== '' || server !== '' || platform !== 'All' || logins.size > 0;
  }, []);
  const doesExternalFilterPass = useCallback(
    (node: IRowNode<BBookPosition>) => (node.data ? rowPassesFilters(node.data) : true),
    [rowPassesFilters],
  );

  const onGridReady = useCallback((_event: GridReadyEvent) => {
    // Grid API now exists — seed it from REST. The WS SNAPSHOT will reconcile
    // on top once the socket connects.
    fetchPositions();
    setGridReady(true);     // the cTrader rows are read once the grid can take them
    setTimeout(() => gridRef.current?.api?.autoSizeAllColumns(), 0);
  }, [fetchPositions]);

  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    const rowData = params.node?.data as BBookPosition | undefined;
    return [
      { name: 'Pin Row to Top', action: () => console.log('Pin:', rowData) },
      { name: 'Unpin Row',      action: () => console.log('Unpin:', rowData) },
      'separator',
      'copy',
      'copyWithHeaders',
      'separator',
      { name: 'Export to CSV',   action: () => params.api.exportDataAsCsv() },
      { name: 'Export to Excel', action: () => params.api.exportDataAsExcel() },
      'separator',
      { name: 'Market Depth Trader', action: () => console.log('Market Depth:', rowData) },
      { name: 'Telegram',            action: () => console.log('Telegram:', rowData) },
    ];
  }, []);

  // ======================
  // STATS — live positions only
  // ======================
  const stats = useMemo(() => {
    const src = filteredPositions;
    const totalPnL  = src.reduce((s, p) => s + (p.profit ?? 0), 0);
    const totalSwap = src.reduce((s, p) => s + (p.swap ?? 0), 0);
    const totalComm = src.reduce((s, p) => s + (p.commission ?? 0), 0);
    const totalLots = src.reduce((s, p) => s + p.volume, 0);
    // Units = lots × contract size. We don't have per-symbol contract sizes
    // wired into the page yet — assume the FX standard 100,000 for now.
    // For symbols where this is wrong (XAU, BTC, indices), the Units number
    // will be off until per-symbol contract sizes are surfaced.
    const totalUnits = totalLots * 100_000;
    return {
      total:       src.length,
      buyCount:    src.filter(p => p.type === 'BUY').length,
      sellCount:   src.filter(p => p.type === 'SELL').length,
      totalVolume: totalLots,
      totalUnits,
      totalPnL,
      netPnL:      totalPnL + totalSwap + totalComm,
      hedgedCount: src.filter(p => p.hedge !== 'No').length,
    };
  }, [filteredPositions]);

  const hasActiveFilters = groupInput.trim() !== '' || symbolInput.trim() !== '' || loginIds.length > 0;

  // The charts sum profit; a position without one counts as nothing there.
  const chartPositions = useMemo(
    () => filteredPositions.map(p => ({ ...p, profit: p.profit ?? 0 })),
    [filteredPositions],
  );

  // cTrader rows are live while the MASTER is connected and the socket is up.
  // Otherwise the rows held are the last known, and the page says so.
  const ctRowsShown = useMemo(() => positions.some(p => p.platform === 'cTrader'), [positions]);
  const ctNote: string | null =
    platformFilter === 'MT5' || !ctRowsShown ? null
    : wsStatus !== 'live' ? 'cTrader positions are not live: the connection to the server is down.'
    : ctMaster && ctMaster.status !== 'CONNECTED' ? `${ctMaster.name} is not connected: its positions are the last known.`
    : null;

  // What an empty grid means, in words. null = there are rows to show.
  const emptyState = useMemo<{ title: string; hint: string } | null>(() => {
    if (error || filteredPositions.length > 0) return null;
    if (rowCount === 0 && wsStatus !== 'live' && platformFilter !== 'cTrader') return null;   // still connecting
    if (hasActiveFilters && rowCount > 0) return { title: 'No positions match the filters', hint: '' };
    if (platformFilter === 'cTrader') {
      if (ctMaster === undefined) return null;                                               // not known yet
      if (ctMaster && ctMaster.status === 'CONNECTED' && !ctKnown) return null;               // still connecting
      return {
        title: 'No open B-Book positions on cTrader',
        hint: ctMaster === null ? 'No cTrader MASTER node is configured.'
          : ctMaster.status !== 'CONNECTED' ? `${ctMaster.name} is not connected.`
          : `${ctMaster.name} holds none.`,
      };
    }
    const mt5Hint = activeNodes.length > 0
      ? 'Assign MT5 groups to the B-Book in Node Management, or there are no open positions.'
      : 'No connected MT5 nodes found. Go to Node Management to connect your MT5 server.';
    if (platformFilter === 'MT5') return { title: 'No open B-Book positions on MT5', hint: mt5Hint };
    return { title: 'No positions in B-Book', hint: mt5Hint };
  }, [error, filteredPositions.length, rowCount, wsStatus, platformFilter, hasActiveFilters, ctMaster, ctKnown, activeNodes.length]);

  // ── Period-scoped derived values for the B-Book card ─────────────────────
  // Source: usePortfolioStats() — the same hook the Portfolio page uses.
  // Period follows the TopBar selector (Today / This Month). Each value is
  // null when the WS hasn't snapped yet, and the card shows '—' for those.
  //
  // Unrealized P/L is already period-scoped on the wire:
  //   period_unrealized = live_floating - baseline_unrealized_eod
  // where the baseline is yesterday's EOD for Today, last-day-of-prev-month
  // for This Month. Computed in PortfolioBroadcaster.cpp.
  //
  // The money cells follow the Platform filter. All: the whole B-Book.
  // MT5 / cTrader: that platform's part (books.B.by_source). A part has the
  // live floating P&L, not the change since the baseline (kept per book only),
  // so the cell reads "Floating P/L" there. A payload without by_source is the
  // MT5 book alone: MT5 then reads the book, cTrader has no figure.
  const bySource = portfolio.bbookBySource;
  const perPlatform = platformFilter !== 'All';
  const part = platformFilter === 'MT5' ? bySource?.MT5 : platformFilter === 'cTrader' ? bySource?.CTRADER : undefined;
  const useBook = !perPlatform || (platformFilter === 'MT5' && !bySource);
  const pnlCell: number | null = useBook ? portfolio.bbook.unrealized : (part?.floating ?? null);
  const pnlLabel = useBook ? 'Unrealized P/L' : 'Floating P/L';
  const realizedCell: number | null = useBook ? portfolio.bbook.realized : (part?.realized ?? null);

  const periodCost = useMemo(() => {
    const src = useBook ? portfolio.bbook : part;
    if (!src) return null;
    const c = src.commissions;
    const s = src.swaps;
    const r = src.rebates;
    if (c == null && s == null && r == null) return null;
    return (c ?? 0) + (s ?? 0) + (r ?? 0);
  }, [useBook, part, portfolio.bbook]);

  // cTrader money that is not in the figures, said beside them.
  const ctMoneyCaveat = useMemo<string | null>(() => {
    if (platformFilter === 'MT5') return null;
    const ct = bySource?.CTRADER;
    if (!ct) return null;
    const lines: string[] = [];
    if (ct.available === false) {
      lines.push('The cTrader master is not connected: its positions count in positions and volume, not in money.');
    } else if ((ct.unpriced ?? 0) > 0) {
      lines.push(`${ct.unpriced} cTrader position${ct.unpriced === 1 ? ' has' : 's have'} no price and ${ct.unpriced === 1 ? 'is' : 'are'} not in the P/L.`);
    }
    if ((ct.no_usd_rate ?? 0) > 0) {
      lines.push(`${ct.no_usd_rate} cTrader position${ct.no_usd_rate === 1 ? ' has' : 's have'} no rate to USD and ${ct.no_usd_rate === 1 ? 'is' : 'are'} not in the P/L.`);
    }
    return lines.length ? lines.join(' ') : null;
  }, [platformFilter, bySource]);

  // Hedge Ratio = C-Book volume / B-Book volume × 100, both for the selected
  // period. Same-units comparison (both are lots from the broadcaster), so
  // the Lots/Units toggle doesn't affect the ratio. Returns null when either
  // side hasn't loaded; '' when B-volume is zero (rendered as the "- -" placeholder).
  const hedgeRatioPct: number | '' | null = useMemo(() => {
    const bVol = portfolio.bbook.volume;
    const cVol = portfolio.cbook.volume;
    if (bVol == null || cVol == null) return null;
    if (bVol === 0) return '';
    return (cVol / bVol) * 100;
  }, [portfolio.bbook.volume, portfolio.cbook.volume]);

  // ======================
  // RENDER
  // ======================
  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#232326' }}>

      {/* Page Header — title on left, M/D toggle + B-Book card right-aligned */}
      <div className="px-4 py-2 border-b border-[#808080] flex items-center justify-between gap-4">
        <div className="shrink-0">
          <h1 className="text-lg font-semibold text-white">B-Book</h1>
        </div>

        {/* M/D toggle + B-Book card grouped on the right.
            Toggle replaces the legacy CardsPeriodSelector that used to sit on the TopBar. */}
        <div className="ml-auto flex items-center gap-2">
          <CardsPeriodToggle />

          {/* ── B-Book card — 7 cells: title | Long/Short | Volume | Unrealized | Realized | Cost | Hedge Ratio ──
             Live cells (positions, Long/Short, Volume) reflect open positions right now.
             Period cells (Unrealized/Realized/Cost/Hedge Ratio) follow the M/D toggle. */}
          <div
            className="inline-flex items-stretch gap-2 rounded px-2 py-1"
            style={{
              backgroundColor: '#252429',
              border: '1px solid #49b3b344',
              borderLeft: '3px solid #49b3b3',
            }}
            title="B-Book card. Live cells reflect open positions; P/L, Cost and Hedge Ratio follow the M/D toggle to the left. P/L and Cost follow the Platform filter."
          >
          {/* Cell 1 — Card name + position count */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white mb-0.5">B-Book</div>
            <div className="text-xs font-mono text-white">{stats.total} pos</div>
          </div>
          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* Cell 2 — Long / Short */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white mb-0.5">Long / Short</div>
            <div className="text-xs font-mono">
              <span style={{ color: '#49b3b3' }}>{stats.buyCount}</span>
              <span className="text-[#505050]"> / </span>
              <span style={{ color: '#e0a020' }}>{stats.sellCount}</span>
            </div>
          </div>
          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* Cell 3 — Volume (Lots / Units toggle).
             Title rendered as a <button> styled to match neighboring <div> titles
             (block, p-0, leading-tight, bg-transparent) so the row baseline aligns.
             Painted in the brand teal #49b3b3 to signal it's clickable. */}
          <div>
            <button
              type="button"
              onClick={() => setVolumeDisplayMode(m => m === 'Lots' ? 'Units' : 'Lots')}
              className="block p-0 m-0 mb-0.5 leading-tight bg-transparent border-0 text-[10px] uppercase tracking-wider hover:opacity-80 transition-opacity cursor-pointer"
              style={{ color: '#49b3b3' }}
              title="Click to toggle between Lots and Units"
            >
              Volume ({volumeDisplayMode})
            </button>
            <div className="text-xs font-mono text-white">
              {volumeDisplayMode === 'Lots'
                ? fmtHdrCompact(stats.totalVolume, '')
                : fmtHdrCompact(stats.totalUnits, '')}
            </div>
          </div>
          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* Cell 4 — Unrealized P/L (whole book, period-scoped: live floating - baseline_eod),
              or a platform's live Floating P/L when the Platform filter is MT5 or cTrader */}
          <div
            title={(useBook
              ? 'Change of the floating P/L since the end of the day before the period.'
              : `Live floating P/L of the ${platformFilter} part of the B-Book.`)
              + (ctMoneyCaveat ? ` ${ctMoneyCaveat}` : '')}
          >
            <div className="text-[10px] uppercase tracking-wider text-white mb-0.5">{pnlLabel}</div>
            {pnlCell != null ? (
              <div className="text-xs font-mono" style={{ color: pnlColor(pnlCell) }}>
                {fmtHdrMoney(pnlCell)}
                {ctMoneyCaveat && <span aria-label="Not every cTrader position is in this figure" style={{ color: '#e0a020' }}> *</span>}
              </div>
            ) : (
              <div className="text-xs font-mono text-[#d2d6e2]">—</div>
            )}
          </div>
          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* Cell 5 — Realized P/L (period rollup) */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white mb-0.5">Realized P/L</div>
            {realizedCell != null ? (
              <div className="text-xs font-mono" style={{ color: pnlColor(realizedCell) }}>
                {fmtHdrMoney(realizedCell)}
              </div>
            ) : (
              <div className="text-xs font-mono text-[#d2d6e2]">—</div>
            )}
          </div>
          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* Cell 6 — Cost (period: commissions + swaps + rebates) */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white mb-0.5">Cost</div>
            {periodCost != null ? (
              <div className="text-xs font-mono" style={{ color: pnlColor(periodCost) }}>
                {fmtHdrMoney(periodCost)}
              </div>
            ) : (
              <div className="text-xs font-mono text-[#d2d6e2]">—</div>
            )}
          </div>
          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* Cell 7 — Hedge Ratio (period: C-Book vol / B-Book vol, %). The C-Book
              covers the whole B-Book, so this stays a whole-book figure. */}
          <div title="C-Book volume over B-Book volume for the period, whole book, both platforms.">
            <div className="text-[10px] uppercase tracking-wider text-white mb-0.5">Hedge Ratio</div>
            {hedgeRatioPct === null ? (
              <div className="text-xs font-mono text-[#d2d6e2]">—</div>
            ) : hedgeRatioPct === '' ? (
              <div className="text-xs font-mono text-[#d2d6e2]">- -</div>
            ) : (
              <div className="text-xs font-mono text-white">
                {hedgeRatioPct.toFixed(1)}%
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-[#3a1f1f] border-b border-[#ff5c5c] text-xs text-[#ff5c5c] flex items-center justify-between">
          <span>⚠ {error}</span>
          <button onClick={fetchPositions} className="ml-4 underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="px-4 py-1.5 border-b border-[#505050] flex items-center gap-4" style={{ backgroundColor: '#2a292c' }}>
        <span className="text-[10px] text-[#666] uppercase tracking-wider font-medium">Filters</span>

        {/* Platform — only the two MASTER nodes count. A platform whose master
            is not connected cannot be chosen. */}
        <label htmlFor="bbook-platform" className="text-xs text-[#d2d6e2]" style={{ marginRight: -8 }}>Platform</label>
        <select
          id="bbook-platform"
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value as PlatformFilter)}
          aria-label="Platform"
          className="w-[190px] bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#49b3b3]"
        >
          <option value="All">All</option>
          <option value="MT5" disabled={!mt5Active}>{mt5Active ? 'MT5' : 'MT5 (not connected)'}</option>
          <option value="cTrader" disabled={!ctActive}>{ctActive ? 'cTrader' : 'cTrader (not connected)'}</option>
        </select>

        {/* Logins from the Cockpit "Critical traders" link (?login_ids=). */}
        {loginIds.length > 0 && (
          <span
            className="inline-flex items-center gap-2 px-2 py-0.5 rounded text-xs text-white"
            style={{ backgroundColor: '#232225', border: '1px solid #49b3b3' }}
            title={`MT5 logins: ${loginIds.join(', ')}`}
          >
            MT5 logins
            <span className="font-mono">
              {loginIds.length <= 3 ? loginIds.join(', ') : loginIds.length}
            </span>
            <button
              onClick={clearLoginIds}
              aria-label="Remove the login filter"
              className="text-[#c8c8cc] hover:text-white transition-colors"
            >
              ✕
            </button>
          </span>
        )}

        <div className="relative">
          <input
            type="text"
            list="bbookgroup-options"
            value={groupInput}
            onChange={(e) => setGroupInput(e.target.value)}
            placeholder="All Groups / All Accounts"
            className="w-[240px] bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white placeholder-[#666] focus:outline-none focus:border-[#49b3b3]"
          />
          <datalist id="bbookgroup-options">
            {uniqueGroups.map(g => <option key={g} value={g} />)}
            {uniqueLogins.map(l => <option key={l} value={l} />)}
          </datalist>
        </div>

        <div className="relative">
          <input
            type="text"
            list="bbooksymbol-options"
            value={symbolInput}
            onChange={(e) => setSymbolInput(e.target.value)}
            placeholder="All Symbols"
            className="w-[240px] bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white placeholder-[#666] focus:outline-none focus:border-[#49b3b3]"
          />
          <datalist id="bbooksymbol-options">
            {uniqueSymbols.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>

        {/* Server selector intentionally hidden — single Master node only.
            filterServer defaults to the master node_name in the activeNodes effect
            above, so the positions list stays narrowed to the master B-Book.
            Re-enable only if/when multi-node B-Book selection becomes a requirement. */}
        {/* <select
          value={filterServer}
          onChange={(e) => setFilterServer(e.target.value)}
          className="w-[200px] bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#49b3b3]"
        >
          {uniqueServers.map(s => <option key={s} value={s}>{s}</option>)}
        </select> */}

        {hasActiveFilters && (
          <button
            onClick={() => { setGroupInput(''); setSymbolInput(''); if (loginIds.length > 0) clearLoginIds(); }}
            className="text-xs text-[#999] hover:text-white transition-colors"
          >
            ✕ Clear
          </button>
        )}

        {/* cTrader rows held while they cannot be live: say so. */}
        {ctNote && (
          <span className="text-xs ml-auto" style={{ color: '#e0a020' }}>{ctNote}</span>
        )}

        {hasActiveFilters && (
          <span className={clsx('text-[10px] text-[#49b3b3] font-mono', !ctNote && 'ml-auto')}>
            {filteredPositions.length} of {positions.length} positions
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden p-2">

        {/* Grid — ALWAYS mounted: its row model is the source of truth for live
            rows and is fed via transactions. rowData is a stable empty array so
            React never re-binds it. Loading/empty states overlay the grid. */}
        <div style={{ position: 'relative', flex: 1, width: '100%' }}>
          <AgGridReact<BBookPosition>
            ref={gridRef}
            theme={gridTheme}
            rowData={EMPTY_ROWS}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            rowHeight={26}
            headerHeight={36}
            getRowId={(p) => getRowId(p.data)}
            rowSelection={rowSelection}
            cellSelection={{ enableHeaderHighlight: true }}
            getContextMenuItems={getContextMenuItems}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            onGridReady={onGridReady}
          />

          {/* Loading overlay — only on initial connect, before any rows */}
          {wsStatus === 'connecting' && rowCount === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-[#999] text-sm"
                 style={{ backgroundColor: '#232326' }}>
              <span className="font-mono">Connecting to live feed…</span>
            </div>
          )}

          {/* Empty state */}
          {emptyState && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-2"
                 style={{ backgroundColor: '#232326', top: 36 }}>
              <p className="text-[#d2d6e2] text-sm">{emptyState.title}</p>
              {emptyState.hint && <p className="text-[#d2d6e2] text-xs">{emptyState.hint}</p>}
            </div>
          )}
        </div>

        {/* Charts - collapsible */}
        <div
          className={clsx(
            'border-t border-[#808080] transition-all duration-300',
            chartsCollapsed ? 'h-[40px]' : 'h-[300px]'
          )}
          style={{ backgroundColor: '#232326' }}
        >
          <BBookCharts
            positions={chartPositions}
            collapsed={chartsCollapsed}
            onToggle={() => setChartsCollapsed(!chartsCollapsed)}
          />
        </div>
      </div>
    </div>
  );
}

export default BBookPage;